import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { useAuthToken } from '../../TokenContext';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useSprintVelocity } from './useCPData';
import MeetingSummariesTimeline from '../MeetingSummariesTimeline';
import MeetingSummariesTableTalent from '../MeetingSummariesTableTalent';
import './ArtemyStyles.css';

export default function SprintPredictions() {
  const token = useAuthToken();
  const { user } = useSelector((s) => s.auth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { velocity, loading: velocityLoading } = useSprintVelocity();
  
  const userType = user?.data?.[0]?.type?.toLowerCase() || '';
  const isCandidate = userType === 'candidate' || userType === 'talent';

  useEffect(() => {
    if (!token || !process.env.REACT_APP_CLIENT_SPRINTUPDATES) { setLoading(false); return; }
    fetch(process.env.REACT_APP_CLIENT_SPRINTUPDATES, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({}),
    })
      .then((r) => r.json())
      .then((json) => setData(json?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  const meta = data?.metadata;

  return (
    <div className="artemy-fade-up">
      <div style={{ marginBottom: 22 }}>
        <div className="artemy-page-title">Sprint Predictions</div>
        <div className="artemy-page-sub">AI-powered sprint health analysis and delivery forecasting</div>
      </div>

      {meta && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 16 }}>
          {[
            { label: 'Active Sprints', value: meta.active_sprints ?? '—', color: 'var(--artemy-accent)' },
            { label: 'Total Sprints', value: meta.total_sprints ?? '—', color: 'var(--artemy-text-primary)' },
            { label: 'Open PRs', value: meta.total_prs ?? '—', color: 'var(--artemy-yellow)' },
            { label: 'Contributors', value: meta.total_github_contributors ?? '—', color: 'var(--artemy-green)' },
          ].map((m) => (
            <div key={m.label} className="artemy-card" style={{ padding: 16 }}>
              <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', marginBottom: 7 }}>{m.label}</div>
              <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 26, fontWeight: 700, color: m.color }}>{m.value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="artemy-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)' }}>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>🤖 AI Sprint Summary</div>
          </div>
          {loading ? (
            <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} sx={{ color: 'var(--artemy-accent)' }} /></div>
          ) : data?.consolidated ? (
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 13, color: 'var(--artemy-text-secondary)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{data.consolidated}</div>
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--artemy-text-dim)', fontSize: 13 }}>
              Sprint data unavailable — check that the sprint updates API is configured.
            </div>
          )}
        </div>

        <div className="artemy-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)' }}>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>Sprint Velocity Trend</div>
          </div>
          <div style={{ padding: 20 }}>
            {velocityLoading ? (
              <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} sx={{ color: 'var(--artemy-accent)' }} /></div>
            ) : velocity && velocity.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={velocity} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--artemy-border)" vertical={false} />
                  <XAxis dataKey="sprint" tick={{ fill: 'var(--artemy-text-dim)', fontSize: 10, fontFamily: 'var(--artemy-font-mono)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--artemy-text-dim)', fontSize: 10, fontFamily: 'var(--artemy-font-mono)' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: 'var(--artemy-bg-surface)', border: '1px solid var(--artemy-border)', borderRadius: 8, color: 'var(--artemy-text-primary)' }}
                  />
                  <Line type="monotone" dataKey="velocity" stroke="var(--artemy-accent)" strokeWidth={3} dot={{ fill: 'var(--artemy-accent)', r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--artemy-text-dim)', fontSize: 13 }}>
                No velocity data available.
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 24, padding: 0 }}>
        {isCandidate ? (
          <MeetingSummariesTableTalent collapsed={false} />
        ) : (
          <MeetingSummariesTimeline collapsed={false} />
        )}
      </div>

      <div style={{ height: 40 }} />
    </div>
  );
}
